/**
 * A safe storage implementation that falls back to memory storage
 * when browser storage is not available due to security restrictions
 */

// Check if storage is available - with extra safety
const isStorageAvailable = (type: 'localStorage' | 'sessionStorage'): boolean => {
  let storage;
  try {
    storage = window[type];
    if (!storage) {
      return false; // Handle cases where window[type] is null/undefined
    }
    const testKey = '__storage_test__';
    storage.setItem(testKey, testKey);
    storage.removeItem(testKey);
    return true;
  } catch (e) {
    // Log the error but return false, indicating unavailability
    console.warn(`Storage check failed for ${type}:`, e);
    return false;
  }
};

// Initialize with the availability check - safely
const localStorageAvailable = isStorageAvailable('localStorage');
const sessionStorageAvailable = isStorageAvailable('sessionStorage');

const safeStorage = {
  memoryStorage: new Map<string, string>(),
  
  getItem(key: string): string | null {
    // Prioritize localStorage if available
    if (localStorageAvailable) {
      try {
        return localStorage.getItem(key);
      } catch (e) {
        console.warn('[safeStorage] Error reading localStorage:', e);
        // Fallback handled below
      }
    }
    // Try sessionStorage if localStorage failed or is unavailable
    if (sessionStorageAvailable) {
      try {
        return sessionStorage.getItem(key);
      } catch (e) {
        console.warn('[safeStorage] Error reading sessionStorage:', e);
        // Fallback to memory storage
        return this.memoryStorage.get(key) || null;
      }
    }
    // If neither browser storage is available/accessible, use memory storage
    return this.memoryStorage.get(key) || null;
  },
  
  setItem(key: string, value: string): void {
    // Try localStorage first
    if (localStorageAvailable) {
      try {
        localStorage.setItem(key, value);
        return; // Successfully set in localStorage
      } catch (e) {
        console.warn('[safeStorage] Error writing to localStorage:', e);
        // Attempt fallback to sessionStorage
      }
    }
    
    // Try sessionStorage if localStorage failed or is unavailable
    if (sessionStorageAvailable) {
      try {
        sessionStorage.setItem(key, value);
        return; // Successfully set in sessionStorage
      } catch (e) {
        console.warn('[safeStorage] Error writing to sessionStorage:', e);
        // Fall back to memory storage
      }
    }
    
    // Fall back to memory storage if all else fails
    console.warn('[safeStorage] Falling back to memory storage for setItem.');
    this.memoryStorage.set(key, value);
  },
  
  removeItem(key: string): void {
    // Attempt to remove from localStorage
    if (localStorageAvailable) {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        console.warn('[safeStorage] Error removing from localStorage:', e);
      }
    }
    
    // Attempt to remove from sessionStorage
    if (sessionStorageAvailable) {
      try {
        sessionStorage.removeItem(key);
      } catch (e) {
        console.warn('[safeStorage] Error removing from sessionStorage:', e);
      }
    }
    
    // Always remove from memory storage as well
    this.memoryStorage.delete(key);
  }
};

export default safeStorage; 